
import { supabase } from "@/lib/supabase";
import { ReportTemplate } from "@/types/reportTypes";
import { handleError, ErrorSeverity, ErrorCategory } from "@/utils/errorHandler";
import { useErrorHandler } from "@/hooks/useErrorHandler";

export interface ReportTemplateRepository {
  getTemplates: (defaultTemplates: ReportTemplate[]) => Promise<ReportTemplate[]>;
  getSavedReports: (organizationId?: string) => Promise<ReportTemplate[]>;
  saveReport: (report: Omit<ReportTemplate, "id"> & { organization_id: string, conversation_history?: any }) => Promise<ReportTemplate>;
  deleteReport: (reportId: string) => Promise<void>;
  getReportById: (reportId: string) => Promise<ReportTemplate | null>;
  getTemplatesByCategory: (category: string) => Promise<ReportTemplate[]>;
}

/**
 * Repository for managing report templates with improved type safety and error handling
 */
export const reportTemplateRepository: ReportTemplateRepository = {
  async getTemplates(defaultTemplates) {
    try {
      if (!Array.isArray(defaultTemplates)) {
        throw new Error("Default templates must be an array");
      }
      
      // Verify template structure
      defaultTemplates.forEach(template => {
        if (!template.id || !template.name || !template.prompt) {
          throw new Error(`Invalid template structure: ${JSON.stringify(template)}`);
        }
      });
      
      return defaultTemplates;
    } catch (error) {
      handleError(
        error,
        "Error retrieving template defaults",
        ErrorSeverity.WARNING,
        { 
          category: ErrorCategory.DATABASE,
          operation: "getTemplates",
          additionalInfo: { templatesCount: defaultTemplates?.length || 0 }
        },
        false
      );
      return [];
    }
  },

  async getSavedReports(organizationId?: string) {
    try {
      if (!organizationId) {
        console.warn("No organization ID provided for getSavedReports");
        return [];
      }
      
      const { data, error } = await supabase
        .from("saved_reports")
        .select("id, name, description, prompt, category")
        .eq("organization_id", organizationId);
        
      if (error) throw error;
      
      if (!data) return [];
      
      // Type validation of returned data
      return data.map(item => {
        // Ensure all required fields exist
        if (!item.id || !item.name || !item.prompt) {
          console.warn(`Invalid report format found: ${JSON.stringify(item)}`);
          throw new Error("Invalid report format in database response");
        }
        
        return {
          id: item.id,
          name: item.name,
          description: item.description || "Saved report",
          prompt: item.prompt,
          category: item.category || "Custom"
        } as ReportTemplate;
      });
    } catch (error) {
      handleError(
        error,
        "Error fetching saved reports",
        ErrorSeverity.ERROR,
        { 
          category: ErrorCategory.DATABASE,
          operation: "getSavedReports",
          additionalInfo: { organizationId }
        },
        true
      );
      return [];
    }
  },

  async saveReport(report) {
    try {
      // Validate input
      if (!report.name || !report.prompt || !report.category || !report.organization_id) {
        throw new Error("Missing required fields for report");
      }
      
      const { data, error } = await supabase
        .from("saved_reports")
        .insert({
          name: report.name,
          description: report.description,
          prompt: report.prompt,
          category: report.category,
          organization_id: report.organization_id,
          conversation_history: report.conversation_history || null
        })
        .select()
        .single();
      
      if (error) throw error;
      if (!data) throw new Error("Failed to save report - no data returned");
      
      return {
        id: data.id,
        name: data.name,
        description: data.description || "",
        prompt: data.prompt,
        category: data.category
      };
    } catch (error) {
      handleError(
        error,
        "Error saving report",
        ErrorSeverity.ERROR,
        { 
          category: ErrorCategory.DATABASE,
          operation: "saveReport",
          additionalInfo: { reportName: report.name }
        },
        true
      );
      throw error; // Re-throw so the UI can handle the failure
    }
  },

  async deleteReport(reportId) {
    try {
      if (!reportId) {
        throw new Error("Report ID is required for deletion");
      }
      
      const { error } = await supabase
        .from("saved_reports")
        .delete()
        .eq("id", reportId);
      
      if (error) throw error;
    } catch (error) {
      handleError(
        error,
        "Error deleting report",
        ErrorSeverity.ERROR,
        { 
          category: ErrorCategory.DATABASE,
          operation: "deleteReport",
          additionalInfo: { reportId }
        },
        true
      );
      throw error; // Re-throw for UI handling
    }
  },

  async getReportById(reportId) {
    try {
      if (!reportId) {
        throw new Error("Report ID is required");
      }
      
      const { data, error } = await supabase
        .from("saved_reports")
        .select("id, name, description, prompt, category, conversation_history")
        .eq("id", reportId)
        .maybeSingle(); // Use maybeSingle instead of single to avoid errors when no record is found
      
      if (error) throw error;
      if (!data) return null;
      
      return {
        id: data.id,
        name: data.name,
        description: data.description || "",
        prompt: data.prompt,
        category: data.category,
        conversation_history: data.conversation_history
      };
    } catch (error) {
      handleError(
        error,
        "Error fetching report by ID",
        ErrorSeverity.WARNING,
        { 
          category: ErrorCategory.DATABASE,
          operation: "getReportById",
          additionalInfo: { reportId }
        },
        false
      );
      return null;
    }
  },

  async getTemplatesByCategory(category) {
    try {
      if (!category) {
        throw new Error("Category is required");
      }
      
      if (category === "All Categories") {
        return this.getTemplates([]);
      }
      
      const { data, error } = await supabase
        .from("saved_reports")
        .select("id, name, description, prompt, category")
        .eq("category", category);
      
      if (error) throw error;
      if (!data) return [];
      
      return data.map(item => ({
        id: item.id,
        name: item.name,
        description: item.description || "",
        prompt: item.prompt,
        category: item.category
      }));
    } catch (error) {
      handleError(
        error,
        "Error fetching templates by category",
        ErrorSeverity.WARNING,
        { 
          category: ErrorCategory.DATABASE,
          operation: "getTemplatesByCategory",
          additionalInfo: { templateCategory: category }
        },
        false
      );
      return [];
    }
  }
};

/**
 * Create a hook to use the report template repository with error handling
 */
export const useReportTemplateRepository = () => {
  const errorHandler = useErrorHandler('ReportTemplateRepository');
  
  return {
    ...reportTemplateRepository,
    
    // Override methods with error handler
    getSavedReports: async (organizationId?: string) => {
      try {
        return await reportTemplateRepository.getSavedReports(organizationId);
      } catch (error) {
        errorHandler.handleError(error, 'getSavedReports', { organizationId });
        return [];
      }
    },
    
    // Continue with other overrides as needed
    // This pattern allows component-specific error handling
  };
};
